import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import classnames from 'classnames';
import { format, zonedTimeToUtc } from 'date-fns-tz';
import { Card } from '@llt-frontend/react-material-card';
import { Button } from '@llt-frontend/react-material-button';
import { getSimpleNumber } from '../../utilities/numbers';
import { getHours } from '../../utilities/time';
import { QuoteListItemPropTypes } from './types';
import addDuration from 'date-fns-duration';
import { parseISO } from 'date-fns';
import useAppContext from '../../hooks/context/use-app-context';
import { formatPrice } from '../packages/repurchase/util';
import { useAnalyticsContext } from '@llt-frontend/react-analytics-context';

// eslint-disable-next-line complexity
export function ClassListItem({ quote, item }) {
  // state
  const params = new URLSearchParams(window.location.search);
  const leadId = params.get('lead_id');
  const clientId = params.get('client_id');
  const entity_type = clientId ? 'client' : 'lead';
  const entity_id = clientId || leadId;

  const { t, ready: translationLoaded } = useTranslation('quotes/class-list-item');
  const { analyticsContext } = useAppContext();
  const { trackEvent } = useAnalyticsContext();

  const { id: quoteId, number_of_payments } = quote || {};

  const {
    id: itemId,
    name,
    parent,
    time_zone,
    start_date,
    end_date,
    available_quantity,
    meeting_schedule,
    content: { images },
    meta: { schedule_summary, links },
    commodity_type,
    subscription,
  } = item;
  const { list_price_cents, quoted_price_cents, currency_code } = quote?.items?.[0] ?? {};
  const hasSale = list_price_cents !== quoted_price_cents;
  const hasInstallments = number_of_payments > 1;
  const installmentPriceCents = quoted_price_cents / number_of_payments;
  const installmentPrice = formatPrice(installmentPriceCents);
  const oldPrice = formatPrice(list_price_cents, currency_code);
  const priceOff = formatPrice(list_price_cents - quoted_price_cents, currency_code);
  const hourlyPriceCents = schedule_summary.schedule_duration_seconds
    ? quoted_price_cents / getHours(schedule_summary.schedule_duration_seconds)
    : undefined;
  const hourlyPrice = hourlyPriceCents ? formatPrice(hourlyPriceCents, currency_code) : undefined;
  let hasRecurringMonths = false;
  const buyLink = `/checkout?q=${quoteId}`;

  // Class/Subscription is considered inactive if it's expired, there are no spots left
  const inactive = Date.UTC() > zonedTimeToUtc(start_date, time_zone) || available_quantity <= 0;
  const isSubscription = commodity_type === 'subscription';
  // Active classes are commodity_type digital and are *not* the parent item
  const activeClass = !inactive && !isSubscription && !parent;

  const monthlyDuration = subscription?.duration;

  let meetingTimeRange = '';
  if (meeting_schedule?.[0]) {
    const parts = meeting_schedule[0].time.split('/');
    const nextMeeting = parts[0];
    const nextMeetingDuration = parts[1];

    const nextMeetingStart = zonedTimeToUtc(nextMeeting, time_zone);
    const nextMeetingEnd = addDuration(nextMeetingStart, nextMeetingDuration);

    meetingTimeRange = `${format(nextMeetingStart, 'h:mm b')} - ${format(
      nextMeetingEnd,
      'h:mm b zzz'
    )}`;
  }

  const imageSrc = images?.product?.responsive_paths && images.product.responsive_paths['2x'];

  // logic for displaying price
  const displayTotalPrice = () => {
    if (
      isSubscription &&
      subscription &&
      subscription.renews &&
      subscription.contract_duration_periods > 1
    ) {
      if (subscription.duration === 1 && subscription.duration_unit === 'month') {
        return `${formatPrice(quoted_price_cents, currency_code)}/month`;
      } else {
        hasRecurringMonths = true;
      }
    }

    return formatPrice(quoted_price_cents, currency_code);
  };

  const totalPrice = displayTotalPrice();
  // side effect

  function trackClick() {
    trackEvent({
      category: 'tutoring-packages',
      action: 'cta-click',
      label: 'membership',
    });
    window['analytics']?.track?.('Element Clicked', {
      element_id: 'pkg-quote-cta-click',
      element_text: t(activeClass || isSubscription ? 'ctaEnroll' : 'ctaSeeDates'),
      element_type: 'button',
      page_path: '/quotes',
    });
  }

  React.useEffect(() => {
    analyticsContext.trackEvent({
      action: 'quote-view-catalog-item',
      category: 'visits',
      domEventData: {
        data: {
          entity_id,
          entity_type,
          catalog_item_quote_id: quoteId,
          catalog_item_quote_item_id: itemId,
        },
      },
    });
  }, [quoteId, itemId, entity_id, entity_type, analyticsContext]);

  // render

  if (!translationLoaded) {
    return null;
  }

  return (
    <Card className="quotes-list-item">
      {imageSrc && (
        <div className="thumbnail">
          <img src={imageSrc} alt={`${name} thumbnail`} />
        </div>
      )}

      <div className="details">
        <h6 className="details__title">{name}</h6>

        {!isSubscription && (
          <div className="details__sessions">
            {t('sessionsDetail', {
              weekCount: schedule_summary.week_count,
              sessionCount: schedule_summary.sessions_per_week,
              sessionLengthMinutes: getSimpleNumber(schedule_summary.session_duration_seconds / 60),
            })}
          </div>
        )}

        {activeClass && (
          <div className="details__schedule">
            <strong>
              {format(parseISO(start_date), 'E, MMM d')}
              {' - '}
              {format(parseISO(end_date), 'E, MMM d')}
            </strong>
            {meetingTimeRange && (
              <>
                <br />
                <span>{meetingTimeRange}</span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="cta">
        {hasInstallments && (
          <div className="cta__installments">
            <Trans
              t={t}
              i18nKey="installments"
              components={{ strong: <strong className="cta__installments--total" /> }}
              values={{ number: number_of_payments, price: installmentPrice }}
            />
          </div>
        )}

        <div
          className={classnames('cta__price', {
            'cta__price--sale': hasSale,
            'cta__price--installments': hasInstallments,
          })}
        >
          <span className="cta__price-total">
            {hasInstallments ? t('totalPrice', { totalPrice }) : totalPrice}
          </span>
          {hourlyPrice && (
            <span className="cta__price-hourly">({t('perHour', { hourlyPrice })})</span>
          )}
        </div>

        {hasRecurringMonths && (
          <div className="cta__price">
            <span className="cta__price-recurring">
              {t('everyNumberOfMonths', { monthlyDuration })}
            </span>
          </div>
        )}

        {hasSale && (
          <div className="cta__price-off">
            <span className="cta__price-off-old">{oldPrice}</span>
            <span className="cta__price-off-amount">{t('off', { priceOff })}</span>
          </div>
        )}

        <div className="cta__button">
          <div className="cta__button-container">
            <Button
              onClick={trackClick}
              contained
              component="a"
              href={activeClass || isSubscription ? buyLink : links.details_page}
            >
              {t(activeClass || isSubscription ? 'ctaEnroll' : 'ctaSeeDates')}
            </Button>
          </div>
        </div>

        {activeClass && (
          <div className="cta__seeOther">
            <a href={links.details_page}>{t('seeOtherDates')}</a>
          </div>
        )}
      </div>
    </Card>
  );
}

ClassListItem.propTypes = QuoteListItemPropTypes;
